/**
 * Type definitions for Live View API
 * Defines interfaces and types used for live streaming functionality
 */

// Live camera channel types
export type LiveCameraChannel = '1' | '2' | '3' | '4'; // 1: Front, 2: Rear, 3: Interior, 4: Exterior

// Live stream status types
export type LiveStreamStatus = 
  | 'free'           // 수신 가능 상태
  | 'busy'           // 수신 불가능 상태  
  | 'LiveStopped'    // 라이브 종료 상태
  | 'onLiveStreaming' // 라이브 스트리밍중
  | 'LiveClosing';   // 디바이스가 라이브 리소스 정리 상태

/**
 * Request parameters for PlayLiveView command
 */
export interface PlayLiveViewParams {
  email: string;
  user_token: string;
  psn: string;
  tokenType?: 'app' | 'web';
  param1?: LiveCameraChannel; // Camera direction (default: '1' for front)
}

/**
 * Response from PlayLiveView API
 */
export interface PlayLiveViewResponse {
  resultcode: string;
  message: string;
  status?: LiveStreamStatus;
  streamname?: string;
}

/**
 * Request parameters for StopLiveView command
 */
export interface StopLiveViewParams {
  email: string;
  user_token: string;
  psn: string;
  tokenType?: 'app' | 'web';
}

/**
 * Response from StopLiveView API
 */
export interface StopLiveViewResponse {
  resultcode: string;
  message: string;
  status?: LiveStreamStatus;
}

/**
 * Request parameters for LiveDirection command
 */
export interface LiveDirectionParams {
  email: string;
  user_token: string;
  psn: string;
  tokenType?: 'app' | 'web';
  param1: LiveCameraChannel; // Camera direction to switch to
}

/**
 * Response from LiveDirection API
 */
export interface LiveDirectionResponse {
  resultcode: string;
  message: string;
  status?: 'free' | 'busy'; // Direction change status
  direction?: LiveCameraChannel; // Current direction (only when status is busy)
  // 추가 필드들 - 디바이스가 새로운 스트림 정보를 반환할 수 있음
  streamname?: string;
  streamURL?: string;
  url?: string;
  channelARN?: string;
  iceServers?: any[];
}

/**
 * Live stream connection state for UI management
 */
export interface LiveStreamState {
  psn: string;
  channel: LiveCameraChannel;
  status: LiveStreamStatus;
  streamname?: string;
  isConnecting: boolean;
  error?: string;
  lastUpdated: Date;
}

/**
 * Live view error codes mapping
 */
export const LIVE_VIEW_ERROR_MESSAGES = {
  BC_ERR_AUTHENTICATION: 'Authentication failed. Please check your credentials.',
  BC_ERR_INVALID_PARAMETER: 'Invalid parameters provided.',
  BC_ERR_SERVER: 'Internal server error occurred.',
  BC_ERR_UNAVAILABLE: 'Device is not available for live streaming.',
  BC_ERR_BUSY: 'Device is currently busy with another live stream.',
  BC_ERR_UNPLUGED: 'Device is unplugged and cannot stream video.',
} as const;

/**
 * Camera channel display names
 */
export const CAMERA_CHANNEL_NAMES: Record<LiveCameraChannel, string> = {
  '1': 'Front Camera',
  '2': 'Rear Camera', 
  '3': 'Interior Camera',
  '4': 'Exterior Camera'
};

/**
 * WebRTC role types for live streaming
 */
export type WebRTCRole = 'VIEWER' | 'MASTER';

/**
 * Request parameters for Live presigned URL
 */
export interface LivePresignURLParams {
  streamName: string;
  role: WebRTCRole;
  psn: string;
}

/**
 * ICE server configuration for WebRTC
 */
export interface ICEServer {
  urls: string[];
  username?: string;
  credential?: string;
}

/**
 * WebRTC endpoints by protocol
 */
export interface EndpointsByProtocol {
  HTTPS: string;
  WSS: string;
}

/**
 * Response from Live presigned URL API
 */
export interface LivePresignURLResponse {
  resultcode: string;
  message: string;
  region: string;
  channelARN: string;
  url: string;
  iceServers: ICEServer[];
  endpointsByProtocol: EndpointsByProtocol;
}

/**
 * Complete WebRTC configuration for live streaming
 */
export interface WebRTCConfig {
  region: string;
  channelARN: string;
  url: string;
  iceServers: ICEServer[];
  endpointsByProtocol: EndpointsByProtocol;
  role: WebRTCRole;
  clientId: string;
}

/**
 * Live streaming workflow result
 */
export interface LiveStreamingWorkflow {
  webrtcConfig: WebRTCConfig;
  streamName: string;
  status: LiveStreamStatus;
}

/**
 * Two-way audio communication status
 */
export type TwoWayStatus = 'free' | 'busy';

/**
 * Request parameters for TwoWayStart command
 */
export interface TwoWayStartParams {
  email: string;
  user_token: string;
  psn: string;
  tokenType?: 'app' | 'web';
}

/**
 * Response from TwoWayStart API
 */
export interface TwoWayStartResponse {
  resultcode: string;
  message: string;
  status?: TwoWayStatus;
}

/**
 * Request parameters for TwoWayStop command
 */
export interface TwoWayStopParams {
  email: string;
  user_token: string;
  psn: string;
  tokenType?: 'app' | 'web';
}

/**
 * Response from TwoWayStop API
 */
export interface TwoWayStopResponse {
  resultcode: string;
  message: string;
  status?: TwoWayStatus;
}